<template>
    <div class="billing-container">
        <!-- Header -->
        <v-card class="teal darken-2 white--text mb-3 elevation-3">
            <v-card-title class="justify-center py-3">
                <h2 class="title font-weight-medium">Pay For Order: {{requested_bill.order_id}}</h2>
            </v-card-title>
        </v-card>

        <!-- Account Information -->
        <v-card class="teal lighten-5 mb-4 elevation-2">
            <v-card-text class="pa-3">
                <v-layout row wrap>
                    <v-flex xs6>
                        <div class="text-xs-center">
                            <div class="caption grey--text text--darken-1">Account Balance</div>
                            <div class="title teal--text text--darken-2 font-weight-bold">{{wallet.balance}} FCFA</div>
                        </div>
                    </v-flex>
                    <v-flex xs6>
                        <div class="text-xs-center">
                            <div class="caption grey--text text--darken-1">Credit Points</div>
                            <div class="title teal--text text--darken-2 font-weight-bold">{{user_credits}} pts</div>
                        </div>
                    </v-flex>
                </v-layout>
                <v-divider class="my-3"></v-divider>
                <p class="caption text-xs-center mb-0 grey--text text--darken-2">
                      <span v-if="payFromAccount">Payment will be deducted from your Wallet</span>
                    <span v-else>Payment via <b>MTN MoMo</b> or <b>Orange Money</b> to <u>Petty Cash SAS</u></span>
                </p>
            </v-card-text>
        </v-card>

        <!-- Main Form -->
        <v-card class="elevation-3">
            <v-card-text class="pa-4">
                <!-- Payment Method Selection -->
                <div class="mb-4">
                    <h3 class="subheading mb-3">
                        <v-icon color="teal darken-1" class="mr-2">payment</v-icon>
                        Choose Payment Method
                    </h3>
                    <v-radio-group v-model="paymentMethod" @change="onPaymentMethodChange">
                      <v-radio
                          label="Pay from my Wallet"
                          value="account"
                          color="teal darken-2"
                      ></v-radio>
                        <v-radio 
                            label="Make a deposit (MTN MoMo / Orange Money)" 
                            value="deposit"
                            color="teal darken-2"
                        ></v-radio>
                    </v-radio-group>
                </div>

                <v-divider class="my-4"></v-divider>

                <!-- Order Cost (Always visible) -->
                <div class="form-field mb-4">
                    <v-layout row align-center>
                        <v-flex shrink class="pr-3">
                            <v-avatar color="teal lighten-5" size="48">
                                <v-icon color="teal darken-1">fas fa-shopping-cart</v-icon>
                            </v-avatar>
                        </v-flex>
                        <v-flex>
                            <v-text-field
                                v-model="total_cost"
                                label="Order Cost"
                                suffix="FCFA"
                                disabled
                                color="teal darken-2"
                                hide-details
                            ></v-text-field>
                        </v-flex>
                    </v-layout>
                </div>

                <!-- Payment Channel (Only for account payment) -->
                <div class="form-field mb-4" v-if="payFromAccount">
                    <v-layout row align-center>
                        <v-flex shrink class="pr-3">
                            <v-avatar color="teal lighten-5" size="48">
                                <v-icon color="teal darken-1">fas fa-credit-card</v-icon>
                            </v-avatar>
                        </v-flex>
                        <v-flex>
                            <v-select
                                v-model="transaction_operator"
                                :items="payment_operators"
                                label="Payment Channel"
                                :rules="[v => !!v || 'Payment channel is required']"
                                color="teal darken-2"
                                hide-details
                            ></v-select>
                        </v-flex>
                    </v-layout>
                </div>

                <!-- Deposit Fields (Only for deposit method) -->
                <div v-if="!payFromAccount">
                    <!-- Amount to Pay -->
                    <div class="form-field mb-4">
                        <v-layout row align-center>
                            <v-flex shrink class="pr-3">
                                <v-avatar color="teal lighten-5" size="48">
                                    <v-icon color="teal darken-1">fas fa-money-bill-wave</v-icon>
                                </v-avatar>
                            </v-flex>
                            <v-flex>
                                <v-text-field
                                    v-model="save_amount"
                                    label="Amount to Pay"
                                    suffix="FCFA"
                                    :rules="[v => !!v || 'Amount is required']"
                                    color="teal darken-2"
                                    hide-details
                                    type="number"
                                ></v-text-field>
                            </v-flex>
                        </v-layout>
                    </div>

                    <!-- Phone Number -->
                    <div class="form-field mb-4">
                        <v-layout row align-center>
                            <v-flex shrink class="pr-3">
                                <v-avatar color="teal lighten-5" size="48">
                                    <v-icon color="teal darken-1">fas fa-phone</v-icon>
                                </v-avatar>
                            </v-flex>
                            <v-flex>
                                <input 
                                    id="phone"
                                    ref="phone_number"
                                    v-model="phone_number"
                                    :disabled="phoneDisabled"
                                    placeholder="Enter phone number"
                                    @click="clearPhoneField()" 
                                    @focus="clearPhoneField()"
                                  
                                 
                                    type="tel" 
                        
                              
                                    name="phone" 
                                   
                                    class="phone-input" 
                                />
                            </v-flex>
                        </v-layout>
                    </div>

                    <!-- Operator Selection (Visual) -->
                    <div class="mb-4">
                        <h3 class="subheading mb-3">
                            <v-icon color="teal darken-1" class="mr-2">fas fa-wrench</v-icon>
                            Select Transaction Operator
                        </h3>
                        <v-layout row wrap justify-center>
                            <v-flex xs6 sm4 class="pa-2">
                                <v-card 
                                    @click="selectMoMo()" 
                                    :class="{'operator-card': true, 'selected': momo_choice}"
                                    hover
                                >
                                    <v-card-text class="text-xs-center pa-2">
                                        <v-img
                                            :src="require('@/assets/mtn-mobile-money-logo.png')"
                                            contain 
                                            height="60"
                                        ></v-img>
                                        <v-checkbox 
                                            v-model="momo_choice" 
                                            label="MTN MoMo" 
                                            hide-details 
                                            class="mt-2"
                                            color="teal darken-2"
                                            readonly
                                        ></v-checkbox>
                                    </v-card-text>
                                </v-card>
                            </v-flex>
                            <v-flex xs6 sm4 class="pa-2">
                                <v-card 
                                    @click="selectOM()" 
                                    :class="{'operator-card': true, 'selected': om_choice}"
                                    hover
                                >
                                    <v-card-text class="text-xs-center pa-2">
                                        <v-img
                                            :src="require('@/assets/om.png')"
                                            contain 
                                            height="60"
                                        ></v-img>
                                        <v-checkbox 
                                            v-model="om_choice" 
                                            label="Orange Money" 
                                            hide-details 
                                            class="mt-2"
                                            color="teal darken-2"
                                            readonly
                                        ></v-checkbox>
                                    </v-card-text>
                                </v-card>
                            </v-flex>
                        </v-layout>
                    </div>
                </div>

                <!-- Shipping Information (Always visible) -->
                <v-divider class="my-4"></v-divider>
                <h3 class="subheading mb-3">
                    <v-icon color="teal darken-1" class="mr-2">local_shipping</v-icon>
                    Delivery Information
                </h3>

                <!-- Shipping Town -->
                <div class="form-field mb-4">
                    <v-layout row align-center>
                        <v-flex shrink class="pr-3">
                            <v-avatar color="teal lighten-5" size="48">
                                <v-icon color="teal darken-1">fas fa-city</v-icon>
                            </v-avatar>
                        </v-flex>
                        <v-flex>
                            <v-select
                                v-model="selected_shipping_town"
                                :items="shipping_towns"
                                label="Delivery Town"
                                item-value="id"
                                item-text="name"
                                :rules="[v => !!v || 'Delivery town is required']"
                                @change="getShippingPoints()"
                                color="teal darken-2"
                                hide-details
                            ></v-select>
                        </v-flex>
                    </v-layout>
                </div>

                <!-- Collection Point -->
                <div class="form-field mb-4">
                    <v-layout row align-center>
                        <v-flex shrink class="pr-3">
                            <v-avatar color="teal lighten-5" size="48">
                                <v-icon color="teal darken-1">fas fa-map-marker-alt</v-icon>
                            </v-avatar>
                        </v-flex>
                        <v-flex>
                            <v-select
                                v-model="selected_shipping_location"
                                :items="shipping_collection_points"
                                label="Collection Point"
                                item-value="id"
                                item-text="name"
                                :rules="[v => !!v || 'Collection point is required']"
                                @change="getDeliveryTime()"
                                color="teal darken-2"
                                hide-details
                            ></v-select>
                        </v-flex>
                        <v-flex shrink class="pl-2">
                            <v-btn 
                                @click="showCollectionHelp()" 
                                icon 
                                small
                                color="teal darken-2"
                            >
                                <v-icon small>help</v-icon>
                            </v-btn>
                        </v-flex>
                    </v-layout>
                </div>

                <!-- Delivery Summary -->
                <v-card v-if="delivery_fee" flat color="grey lighten-4" class="mt-4 delivery-info">
                    <v-card-text class="pa-3">
                        <v-layout row wrap align-center>
                            <v-flex xs6 class="pr-2">
                                <div class="text-xs-center">
                                    <v-icon color="teal darken-1" small class="mb-1">local_shipping</v-icon>
                                    <div class="caption grey--text text--darken-1">Delivery Fee</div>
                                    <div class="subheading font-weight-bold">{{delivery_fee_amount}} FCFA</div>
                                </div>
                            </v-flex>
                            <v-flex xs6 class="pl-2">
                                <div class="text-xs-center">
                                    <v-icon color="teal darken-1" small class="mb-1">schedule</v-icon>
                                    <div class="caption grey--text text--darken-1">Delivery Time</div>
                                    <div class="subheading font-weight-bold">{{delivery_time}}</div>
                                </div>
                            </v-flex>
                        </v-layout>
                        <v-divider class="my-2"></v-divider>
                        <div class="text-xs-center">
                            <div class="caption grey--text text--darken-1">Total Amount</div>
                            <div class="title teal--text text--darken-2 font-weight-bold">{{totalAmount}} FCFA</div>
                        </div>
                    </v-card-text>
                </v-card>

                <!-- Balance Warning (For account payment) -->
            <v-alert
                v-if="payFromAccount && isBalanceInsufficient"
                type="warning"
                :value="true"
                class="mt-4"
                outline
            >
                <!-- CHANGED: Use wallet.balance instead of campaign.saved_amount -->
                Insufficient balance. You need {{totalAmount}} FCFA but have {{wallet.balance}} FCFA.
            </v-alert>
            </v-card-text>

            <!-- Action Buttons -->
            <v-card-actions class="pa-4 pt-0">
                <v-btn 
                    large
                    color="teal darken-2" 
                    class="white--text elevation-2 primary-action-btn"
                    @click="initiatePayment()"
                    :disabled="payFromAccount && isBalanceInsufficient"
                    :loading="isProcessing"
                    block
                >
                    <v-icon left>check_circle</v-icon>
                    <span v-if="payFromAccount">Pay from Account ({{totalAmount}} FCFA)</span>
                    <span v-else>Proceed to Deposit</span>
                </v-btn>
            </v-card-actions>
        </v-card>

        <!-- Help Dialog -->
        <v-dialog v-model="helpDialog" max-width="400px">
            <v-card>
                <v-card-title class="teal darken-2 white--text">
                    <span class="headline">Collection Point</span>
                </v-card-title>
                <v-card-text class="pa-4">
                    <p class="body-1">
                        Select the specific location where you will pick up your items. 
                        The delivery fee and estimated time will be displayed based on your selection.
                    </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="teal darken-2" flat @click="helpDialog = false">OK</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <!-- Account Payment Confirmation Dialog -->
               <v-dialog v-model="confirmDialog" max-width="500px" persistent>
              <v-card>
                  <v-card-title class="teal darken-2 white--text">
                      <!-- CHANGED: Title text -->
                      <span class="headline">Confirm Payment from Wallet</span>
                  </v-card-title>
                  <v-card-text class="pa-4">
                      <p class="body-1 mb-3">
                          <!-- CHANGED: Message text -->
                          You are about to pay for order <b>{{requested_bill.order_id}}</b> from your Wallet
                      </p>
                      <v-list>
                          <v-list-tile>
                              <v-list-tile-content>
                                  <v-list-tile-sub-title>Order Cost</v-list-tile-sub-title>
                                  <v-list-tile-title>{{total_cost}} FCFA</v-list-tile-title>
                              </v-list-tile-content>
                          </v-list-tile>
                          <v-list-tile>
                              <v-list-tile-content>
                                  <v-list-tile-sub-title>Delivery Fee</v-list-tile-sub-title>
                                  <v-list-tile-title>{{delivery_fee_amount}} FCFA</v-list-tile-title>
                              </v-list-tile-content>
                          </v-list-tile>
                          <v-divider></v-divider>
                          <v-list-tile>
                              <v-list-tile-content>
                                  <v-list-tile-sub-title>Total Amount</v-list-tile-sub-title>
                                  <v-list-tile-title class="title teal--text text--darken-2">{{totalAmount}} FCFA</v-list-tile-title>
                              </v-list-tile-content>
                          </v-list-tile>
                          <v-list-tile>
                              <v-list-tile-content>
                                  <!-- CHANGED: Use wallet.balance -->
                                  <v-list-tile-sub-title>Current Wallet Balance</v-list-tile-sub-title>
                                  <v-list-tile-title>{{wallet.balance}} FCFA</v-list-tile-title>
                              </v-list-tile-content>
                          </v-list-tile>
                          <v-list-tile>
                              <v-list-tile-content>
                                  <v-list-tile-sub-title>Balance After Payment</v-list-tile-sub-title>
                                  <v-list-tile-title class="font-weight-bold">{{balanceAfter}} FCFA</v-list-tile-title>
                              </v-list-tile-content>
                          </v-list-tile>
                      </v-list>
                  </v-card-text>
                  <v-card-actions>
                      <v-btn color="grey" flat @click="confirmDialog = false" :disabled="isProcessing">
                          Cancel
                      </v-btn>
                      <v-spacer></v-spacer>
                      <v-btn
                          color="teal darken-2"
                          class="white--text"
                          @click="processAccountPayment()"
                          :loading="isProcessing"
                      >
                          Confirm & Pay
                      </v-btn>
                  </v-card-actions>
              </v-card>
          </v-dialog>

        <!-- Deposit Confirmation Dialog -->
        <v-dialog v-model="depositDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="teal darken-2 white--text">
                    <span class="headline">Confirm Deposit</span>
                </v-card-title>
                <v-card-text class="pa-4">
                    <p class="body-1 mb-3">
                        By clicking <b>Confirm</b>, a payment transaction of <b>{{save_amount}} FCFA</b> 
                        will be triggered through <b>{{transaction_operator}}</b> from <b>{{phone_number}}</b>
                    </p>
                    <div v-if="transaction_operator === 'MTN Mobile Money' || transaction_operator === 'Orange Money'">
                        <v-alert type="info" :value="true" outline class="mb-2">
                            If you are facing difficulties, send manually through the code below:
                        </v-alert>
                        <p class="pl-2 mb-0 pt-1 red--text text--darken-2 font-weight-bold">{{transfer_code}}</p>
                        <p class="pl-2 mb-0 pt-1" v-if="transaction_operator === 'MTN Mobile Money'">- Choose reference 1</p>
                        <p class="pl-2 mb-0 pt-1" v-if="transaction_operator === 'Orange Money'">- Input transaction amount</p>
                        <p class="pl-2 mb-0 pt-1">- Click <b>Confirm</b> below</p>
                    </div>
                </v-card-text>
                <v-card-actions>
                    <v-btn color="grey" flat @click="depositDialog = false" :disabled="isProcessing">
                        Cancel
                    </v-btn>
                    <v-spacer></v-spacer>
                    <a id="dial_transaction">
                        <v-btn 
                            color="teal darken-2" 
                            class="white--text" 
                            @click="processDeposit()"
                            :loading="isProcessing"
                        >
                            Confirm
                        </v-btn>
                    </a>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            // Payment method selection
            paymentMethod: 'account', // 'account' or 'deposit'
            phoneDisabled: true, // Démarrer avec true si mode account par défaut
            
            // Account data
            wallet: { balance: 0 },
            user_credits: 0,
            requested_bill: {},
            payment_operators: [],
            
            // Shipping data
            shipping_towns: [],
            shipping_collection_points: [],
            shipping_data: [],
            selected_shipping_town: "",
            selected_shipping_location: "",
            delivery_time: "",
            delivery_fee: "",
            delivery_fee_amount: 0,
            
            // Payment data
            total_cost: "",
            transaction_operator: "My Account", // Initialize with "My Account" for default mode
            payment_ref: "",
            
            // Deposit specific
            save_amount: "",
            phone_number: "",
            phoneInput: null,
            momo_choice: false,
            om_choice: false,
            transfer_code: "",
            
            // UI state
            helpDialog: false,
            confirmDialog: false,
            depositDialog: false,
            isProcessing: false,
            requested_order_id: "",
        }
    },

    computed: {
        payFromAccount() {
            return this.paymentMethod === 'account'
        },
        
        totalAmount() {
            const orderCost = Number(this.total_cost) || 0
            const deliveryFee = Number(this.delivery_fee_amount) || 0
            return orderCost + deliveryFee
        },

        isBalanceInsufficient() {
            const balance = Number(this.wallet.balance) || 0
            return balance < this.totalAmount
        },

        balanceAfter() {
            return Number(this.wallet.balance) - this.totalAmount
        }
    },

    watch: {
        // Auto-sync transaction_operator when user selects MTN/Orange in deposit mode
        momo_choice(newVal) {
            if (newVal && !this.payFromAccount) {
                this.transaction_operator = "MTN Mobile Money"
            }
        },
        om_choice(newVal) {
            if (newVal && !this.payFromAccount) {
                this.transaction_operator = "Orange Money"
            }
        },
        // Auto-select operator when transaction_operator changes in account mode
        transaction_operator(newVal) {
            if (this.payFromAccount) {
                // Sync visual selection for deposit mode if user switches back
                if (newVal === "MTN Mobile Money") {
                    this.momo_choice = true
                    this.om_choice = false
                } else if (newVal === "Orange Money") {
                    this.momo_choice = false
                    this.om_choice = true
                }
            }
        }
    },

    mounted() {
        this.getRequestedBillDetail()
        this.getWalletDetail()
        this.getUserCredits()
        this.getOperators()
        this.getShippingTowns()
        this.getPaymentPaymentRef()
        
        // Set initial phone state based on payment method
        if (this.payFromAccount) {
            this.phoneDisabled = true
            this.phone_number = ""
        } else {
            this.phoneDisabled = false
            this.phone_number = ""
            // Initialize phone input for deposit mode
            this.$nextTick(() => {
                this.initialiseIntel()
            })
        }
        
        document.title = "PettyCash | Pay Order"
    },

    methods: {
        onPaymentMethodChange() {
            // Reset deposit fields when switching to account payment
            if (this.payFromAccount) {
                this.save_amount = ""
                this.transaction_operator = "My Account"
                this.phoneDisabled = true
                this.phone_number = ""
                
                // Destroy phone input when switching to account mode
                if (this.phoneInput) {
                    try {
                        this.phoneInput.destroy()
                        this.phoneInput = null
                    } catch (e) {
                        console.log("Error destroying phone input:", e)
                    }
                }
            } else {
                // Switching to deposit mode
                this.save_amount = this.totalAmount || this.total_cost
                this.phoneDisabled = false
                this.phone_number = ""
                
                // Reset transaction operator or keep MTN/Orange if already selected
                if (this.transaction_operator === "My Account") {
                    this.transaction_operator = ""
                }
                
                // Auto-select operator in deposit mode if already chosen
                if (this.transaction_operator === "MTN Mobile Money") {
                    this.momo_choice = true
                    this.om_choice = false
                } else if (this.transaction_operator === "Orange Money") {
                    this.momo_choice = false
                    this.om_choice = true
                }
                
                // Initialize phone input when switching to deposit mode
                this.$nextTick(() => {
                    this.initialiseIntel()
                })
            }
        },

        async getRequestedBillDetail() {
            this.$store.commit('setIsLoading', true)
            const requested_order_id = this.$route.params.requested_order_id
            this.requested_order_id = requested_order_id
            
            await axios.get(`/api/v1/market_place/get/user/requested/bills/${requested_order_id}/`)
                .then(response => {
                    this.requested_bill = response.data
                    this.payment_ref = response.data.payment_ref
                    this.total_cost = this.requested_bill.total_cost
                    
                    if(response.data.shipping_location && response.data.shipping_location.length > 5) {
                        this.$router.push('/market_place/shopping_bag/items/transactions/')
                        this.showError("Error: This billing request has already been processed.")
                    }
                })
                .catch(this.handleError)
            
            this.$store.commit('setIsLoading', false)
        },

       async getWalletDetail() {
            await axios.get('/api/v1/market_place/wallet/get/balance/')
                .then(response => {
                    this.wallet = response.data
                })
                .catch(this.handleError)
        },

        async getUserCredits() {
            await axios.get('/api/v1/home/get/user/credits')
                .then(response => {
                    this.user_credits = response.data[0]["credit"]
                })
                .catch(this.handleError)
        },
        
        async getOperators() {
            await axios.get('/api/v1/market_place/get/payment/operators')
                .then(response => {
                    this.payment_operators = response.data
                })
                .catch(this.handleError)
        },

                async getShippingTowns() {
                    await axios.get('/api/v1/market_place/get/shipping/towns')
                        .then(response => {
                            this.shipping_towns = response.data
                        })
                        .catch(this.handleError)
                },
        
                async getShippingPoints() {
                  if (!this.selected_shipping_town) {
                      this.showError("Please select a delivery town first")
                      return
                  }

                  this.$store.commit('setIsLoading', true)
                  this.selected_shipping_location = ""
                  this.delivery_fee = ""
                  this.delivery_fee_amount = 0
                  this.delivery_time = ""

                  try {
                      const response = await axios.get(`/api/v1/market_place/get/shipping/points/${this.selected_shipping_town}`, {
                          timeout: 10000 // 10 seconds timeout
                      })

                      if (response.data && response.data.length > 0) {
                          this.shipping_collection_points = response.data
                          this.shipping_data = response.data
                      } else {
                          this.showError("No collection points available for this town")
                          this.shipping_collection_points = []
                          this.shipping_data = []
                      }
                  } catch (error) {
                      console.error("Error loading collection points:", error)

                      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                          this.showError("Connection timeout. Please check your internet and try again.")
                      } else if (!error.response) {
                          this.showError("Network error. Please check your internet connection and try again.")
                      } else {
                          this.handleError(error)
                      }

                      // Reset collection points on error
                      this.shipping_collection_points = []
                      this.shipping_data = []
                  } finally {
                      this.$store.commit('setIsLoading', false)
                  }
              },
        
        getDeliveryTime() {
            const location = this.shipping_data.find(
                item => item.id === this.selected_shipping_location
            )
            if (location) {
                this.delivery_fee_amount = location.fee
                this.delivery_fee = `${location.fee} FCFA`
                this.delivery_time = `${location.time_interval_days} days`
                
                // Update save_amount if in deposit mode
                if (!this.payFromAccount) {
                    this.save_amount = this.totalAmount
                }
            }
        },

        async getPaymentPaymentRef() {
            await axios.get(`/api/v1/market_place/create/order/payment_ref/${this.requested_order_id}/`)
                .then(response => {
                    this.payment_ref = response.data
                })
                .catch(this.handleError)
        },

        initialiseIntel() {
            // Only initialize if phone input field exists
            const phoneInputField = document.querySelector("#phone");
            if (!phoneInputField) {
                console.log("Phone input field not found in DOM")
                return
            }

            // Destroy existing instance if any
            if (this.phoneInput) {
                try {
                    this.phoneInput.destroy()
                } catch (e) {
                    console.log("No existing intlTelInput to destroy")
                }
                this.phoneInput = null
            }

            // Check if intlTelInput library is available
            if (!window.intlTelInput) {
                console.error("intlTelInput library not loaded")
                // Try to load it dynamically if not present
                this.loadIntlTelInputLibrary()
                return
            }

            function getIp(callback) {
                fetch('https://ipinfo.io/json?token=fa5e74d3e65812', { 
                    headers: { 'Accept': 'application/json' }
                })
                .then((resp) => resp.json())
                .catch(() => {
                    return { country: 'cm' };
                })
                .then((resp) => callback(resp.country));
            }

            try {
                this.phoneInput = window.intlTelInput(phoneInputField, {
                    initialCountry: "cm",
                    geoIpLookup: getIp,
                    preferredCountries: ["cm", "us", "uk", "ci", "cg", "cf", "td", "ng", "gq", "ga"],
                    utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/utils.js",
                    separateDialCode: true,
                    autoPlaceholder: "polite"
                });
                
                console.log("Phone input initialized successfully")
                
                // Clear the placeholder text
                phoneInputField.value = ""
                this.phone_number = ""
                
            } catch (error) {
                console.error("Error initializing intlTelInput:", error)
            }
        },

        loadIntlTelInputLibrary() {
            // Load CSS
            const link = document.createElement('link')
            link.rel = 'stylesheet'
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/css/intlTelInput.css'
            document.head.appendChild(link)
            
            // Load JS
            const script = document.createElement('script')
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.8/js/intlTelInput.min.js'
            script.onload = () => {
                console.log("intlTelInput library loaded")
                this.$nextTick(() => {
                    this.initialiseIntel()
                })
            }
            document.head.appendChild(script)
        },

        clearPhoneField() {
            if (this.phone_number === "Enter Phone Number" || this.phone_number === "") {
                this.phone_number = ""
                // Clear the intl-tel-input display
                if (this.phoneInput) {
                    this.phoneInput.setNumber("")
                }
                // Also clear the actual input field
                const phoneInputField = document.querySelector("#phone");
                if (phoneInputField) {
                    phoneInputField.value = ""
                }
            }
        },

        selectMoMo() {
            this.momo_choice = true
            this.om_choice = false
            this.transaction_operator = "MTN Mobile Money"
        },

        selectOM() {
            this.momo_choice = false
            this.om_choice = true
            this.transaction_operator = "Orange Money"
        },

        initiatePayment() {
            // Common validation
            if (!this.selected_shipping_town) {
                this.showError("Please select the delivery town")
                return
            }
            if (!this.selected_shipping_location) {
                this.showError("Please select the collection point")
                return
            }

            if (this.payFromAccount) {
                // Account payment - no phone number needed
                if (!this.transaction_operator) {
                    this.showError("Please select the payment channel")
                    return
                }
                if (this.isBalanceInsufficient) {
                    this.showError("Insufficient balance. Please switch to deposit method.")
                    return
                }
                this.confirmDialog = true
            } else {
                // Deposit payment - validate all deposit fields
                if (!this.transaction_operator) {
                    this.showError("Please select the payment channel")
                    return
                }
                if (!this.save_amount || String(Number(this.save_amount)) === "NaN") {
                    this.showError("Please input a valid amount")
                    return
                }
                if (!this.momo_choice && !this.om_choice) {
                    this.showError("Please select MTN MoMo or Orange Money")
                    return
                }
                
                // Validate phone number only for deposit mode
                if (!this.phoneInput) {
                    this.showError("Please initialize phone input")
                    return
                }
                
                const currentPhone = this.phoneInput.getNumber()
                if (!currentPhone || currentPhone === "" || currentPhone === "Enter Phone Number") {
                    this.showError("Please enter a phone number")
                    return
                }
                
                if (!this.phoneInput.isValidNumber()) {
                    this.showError("Please input a valid phone number")
                    return
                }
                
                this.prepareTelDial()
                this.depositDialog = true
            }
        },

        prepareTelDial() {
            if (!this.phoneInput) {
                this.showError("Phone input not initialized")
                return
            }
            
            this.phone_number = this.phoneInput.getNumber()
            
            if (!this.phone_number || this.phone_number === "Enter Phone Number") {
                this.showError("Please enter a valid phone number")
                return
            }
            
            let href_tel = ""
            
            if (this.transaction_operator === "MTN Mobile Money") {
                this.transfer_code = "*126*4*" + this.$store.state.MerchantCodeMTN + "*" + this.save_amount + "#"
                href_tel = "tel:*126*4*" + this.$store.state.MerchantCodeMTN + "*" + this.save_amount + "%23"
            } else if (this.transaction_operator === "Orange Money") {
                this.transfer_code = "#150*47*" + this.$store.state.MerchantCodeOrange + "*" + this.save_amount + "#"
                href_tel = "tel:%23150*47*" + this.$store.state.MerchantCodeOrange + "*" + this.save_amount + "%23"
            }
            
            const dialElement = document.getElementById('dial_transaction')
            if (dialElement) {
                dialElement.setAttribute('href', href_tel)
                dialElement.setAttribute('target', "_blank")
            }
        },

      async processAccountPayment() {
            this.isProcessing = true

            let formData = new FormData()
            formData.append('transaction_operator', this.transaction_operator)
            formData.append('selected_shipping_town', this.selected_shipping_town)
            formData.append('selected_shipping_location', this.selected_shipping_location)
            formData.append('payment_from_account', 'true')
            formData.append('total_amount', this.totalAmount)
            formData.append('order_cost', this.total_cost)
            formData.append('delivery_fee', this.delivery_fee_amount)

            await axios.post(`/api/v1/market_place/complete/bill/transaction/${this.requested_order_id}/`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            })
                .then(response => {
                    this.confirmDialog = false

                    this.$store.commit('setSnackBarMessage', response.data[0].text)
                    this.$store.commit('setSnackBarColor', response.data[0].color)
                    this.$store.commit('activateSnackBar', true)

                    if (response.data[0].valid) {
                        // CHANGED: Update local wallet balance instead of campaign
                        this.wallet.balance = this.balanceAfter

                        // Redirect to transactions page after successful payment
                        setTimeout(() => {
                            this.$router.push('/market_place/shopping_bag/items/transactions/')
                        }, 2000)
                    }
                })
                .catch(this.handleError)

            this.isProcessing = false
        },
        async processDeposit() {
            if (!this.payment_ref) {
                this.showError("An error occurred with transaction, please try again later.")
                window.location.reload()
                return
            }

            // Validate phone number before processing
            if (!this.phoneInput) {
                this.showError("Phone input not initialized. Please refresh the page.")
                return
            }

            const phoneNumber = this.phoneInput.getNumber()
            if (!phoneNumber || phoneNumber === "" || phoneNumber === "Enter Phone Number") {
                this.showError("Please enter a valid phone number")
                return
            }

            if (!this.phoneInput.isValidNumber()) {
                this.showError("The phone number you entered is not valid")
                return
            }

            this.isProcessing = true
            let formData = new FormData()
            formData.append('payment_ref', this.payment_ref)
            formData.append('save_amount', this.save_amount)
            formData.append('phone_number', phoneNumber)
            formData.append('transaction_operator', this.transaction_operator)

            await axios.post(`/api/v1/market_place/pay/order/${this.requested_order_id}/`, formData, {
                headers: {'Content-Type': 'multipart/form-data'}
            })
                .then(response => {
                    this.depositDialog = false
                    
                    this.$store.commit('setSnackBarMessage', response.data[0].text)
                    this.$store.commit('setSnackBarColor', response.data[0].color)
                    this.$store.commit('activateSnackBar', true)
                    this.$store.commit('setSnackBarTimeOut', 8000)

                    if (response.data[0].valid) {
                        // Show verification popup using store
                        this.$store.commit('showPaymentVerification', {
                            amount: this.save_amount,
                            operator: this.transaction_operator,
                            phoneNumber: phoneNumber,
                            feature: 'marketplace_payment_deposit',
                            id: this.requested_order_id
                        })
                    }
                })
                .catch(this.handleError)
            
            this.isProcessing = false
        },

        showCollectionHelp() {
            this.helpDialog = true
        },

        showError(message) {
            this.$store.commit('setSnackBarMessage', message)
            this.$store.commit('setSnackBarColor', "red darken-4")
            this.$store.commit('activateSnackBar', true)
        },

        handleError(error) {
            this.isProcessing = false
            if (error.response) {
                this.showError(`${error.response.status} ${error.response.statusText}`)
            } else {
                this.showError("An error occurred. Please check your connection and try again.")
            }
        }
    }
}
</script>

<style scoped>
.billing-container {
    max-width: 700px;
    margin: 0 auto;
    padding: 16px;
}

.form-field {
    transition: all 0.3s ease;
}

.form-field:hover {
    transform: translateX(2px);
}

.phone-input {
    width: 100% !important;
    padding: 12px;
    border: none;
    border-bottom: 1px solid #9e9e9e;
    font-size: 16px;
    transition: border-color 0.3s;
}

.phone-input:focus {
    outline: none;
    border-bottom-color: #00897b;
}

.operator-card {
    cursor: pointer;
    transition: all 0.3s ease;
    border: 2px solid transparent;
}

.operator-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.operator-card.selected {
    border-color: #00897b;
    background-color: #e0f2f1;
}

.delivery-info {
    border-radius: 8px;
    border-left: 4px solid #00897b;
}

.primary-action-btn {
    border-radius: 8px !important;
    text-transform: none;
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.5px;
    padding: 24px 0 !important;
}

.primary-action-btn:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 137, 123, 0.4) !important;
    transition: all 0.3s ease;
}

.v-avatar {
    transition: all 0.3s ease;
}

.form-field:hover .v-avatar {
    transform: scale(1.05);
}

.elevation-2 {
    box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
}

.elevation-3 {
    box-shadow: 0 3px 12px rgba(0,0,0,0.12) !important;
}

/* Radio group styling */
.v-radio .v-label {
    font-size: 15px;
    font-weight: 500;
}
</style>